"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import type { ProductDetails, Variant } from "@raumagarn/formatters";
import { useBackInStock, useSelection } from "@trydig/centra/client";
import Image from "next/image";
import { useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import z from "zod";
import type { AddToCartButtonProps } from "~/components/ui/AddToCartButton/AddToCartButton";
import { Modal } from "~/features/modal";
import { useVariantContext } from "~/features/products/VariantContext";
import { useModal } from "~/lib/hooks/useModal";
import styles from "./NotifyMeModal.module.css";

export type NotifyProduct = {
	productName: string | undefined;
	media: NonNullable<Variant["media"]>[number] | undefined;
	variantName: string | undefined;
	selectedSize: NonNullable<ProductDetails["items"]>[number] | undefined;
};

export interface NotifyProductStore {
	product: NotifyProduct | undefined;
	setNotifyProduct: (product: NotifyProduct) => void;
}

export const NotifyMeModal = (props: AddToCartButtonProps) => {
	const modal = useModal("notify", () => {}, true);

	return (
		<Modal isOpen={modal.isOpen} callback={modal.closeModal} className={styles["cart-modal"]}>
			<NotifyMeForm {...props} />
		</Modal>
	);
};

const schema = z.object({
	email: z.string().email(),
});

type AddToCartInput = z.infer<typeof schema>;

const NotifyMeForm = ({ selectedSize: externalSelectedSize }: AddToCartButtonProps) => {
	const [success, setSuccess] = useState(false);
	const [error, setNotifyError] = useState(false);

	const { currentVariant } = useVariantContext();
	const selectedSize = currentVariant?.items?.find(
		(size) => size.name === externalSelectedSize?.name,
	);

	const formattedProductDetails: NotifyProduct = {
		productName: currentVariant?.name,
		media: currentVariant?.media?.[0],
		variantName: currentVariant?.variantName,
		selectedSize,
	};

	const { register, handleSubmit, resetField } = useForm<AddToCartInput>({
		resolver: zodResolver(schema),
	});

	const { mutateAsync } = useBackInStock();

	const { data: countryData } = useSelection({
		select: (data) => {
			return {
				...data.location,
			};
		},
	});

	const onSubmit: SubmitHandler<AddToCartInput> = async ({ email }) => {
		if (!email || !selectedSize?.item) return;

		return await mutateAsync(
			{
				email,
				item: selectedSize.item,
				shipTo: {
					country: countryData?.country ?? "",
				},
			},
			{
				onSuccess: () => {
					setSuccess(true);
					resetField("email");
				},

				onError: () => {
					setNotifyError(true);
				},
			},
		);
	};

	return (
		<div className={styles.root}>
			{success && (
				<div className={styles.successMessage}>
					<p>Takk! Vi sender deg en e-post når produktet er tilbake på lager.</p>
				</div>
			)}

			{error && !success && (
				<div className={styles.errorMessage}>
					<p>Noe gikk galt. Vennligst prøv igjen senere.</p>
				</div>
			)}

			{!success && (
				<>
					<div className={styles.productInfo}>
						{!!formattedProductDetails.media?.href && (
							<div className={styles.imageContainer}>
								<Image
									src={formattedProductDetails.media.href}
									alt={`${formattedProductDetails.productName} ${formattedProductDetails.variantName}`}
									width={115}
									height={115}
								/>
							</div>
						)}
						<div className={styles.details}>
							<p className={styles.productName}>{formattedProductDetails.productName}</p>
							<p className={styles.productMeta}>Størrelse: {selectedSize?.name}</p>
							<p className={styles.productMeta}>Farge: {formattedProductDetails.variantName}</p>
						</div>
					</div>

					<div className={styles.formSection}>
						<h3 className={styles.formTitle}>Varsle meg når produktet er tilbake</h3>
						<form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
							<input
								className={styles.input}
								type="email"
								placeholder="Skriv inn e-post"
								{...register("email", { required: true })}
							/>
							<button type="submit" className={styles.submitButton}>
								Varsle meg
							</button>
						</form>
					</div>
				</>
			)}
		</div>
	);
};
